import { useApolloClient } from '@apollo/client';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { formatDate } from '@orion/fe-utils';

import type { RootState } from 'store';

import { CIAnalytics } from 'common/analytics/analytics.provider';
import { AuthActions } from 'common/auth/actions';
import { userMustAcceptPrivacyPolicy } from 'common/auth/privacyPolicyAcceptance';
import { CIError } from 'common/error/error_util';
import { usePageStatus } from 'common/hooks/use-page-status';
import { CILogger } from 'common/log/log.provider';
import { preprocessHtmlText } from 'common/utils/common.utils';
import { CISecondaryButton, CITertiaryButton } from 'common/viewcomp/forms/buttons';
import { SessionTimeoutDialog } from 'common/viewcomp/layout/SessionTimeoutDialog';

import { CheckPPTranslationQuery, GetPolicyQuery } from '../queries';

interface SecondaryAction {
    label: ReactNode;
    onClick: () => void;
}

interface PolicyViewerProps {
    onAccepted: () => void;
    secondaryAction?: SecondaryAction;
    fullViewportBackground?: boolean;
    forceLoading?: boolean;
}

interface State {
    loading: boolean;
    policyData: string;
    policy: {
        title: string;
        updatedAt: string;
    };
    isButtonDisabled: boolean;
    isLoading: boolean;
    isPolicyClick: boolean;
}

const initialState: State = {
    loading: true,
    policyData: null,
    policy: {
        title: '',
        updatedAt: '',
    },
    isButtonDisabled: true,
    isLoading: false,
    isPolicyClick: false,
};

export const PolicyViewer = ({
    onAccepted,
    secondaryAction,
    fullViewportBackground = false,
    forceLoading = false,
}: PolicyViewerProps): ReactElement => {
    const { t } = useTranslation('policy');
    const client = useApolloClient();
    const pageStatus = usePageStatus();
    const dispatch = useDispatch();
    const policyRef = useRef<HTMLDivElement>(null);

    const [state, setState] = useState<State>(initialState);
    const user = useSelector((st: RootState) => st.auth?.activeUser);
    const authData = useSelector((st: RootState) => st.auth);
    const currentPolicy = useSelector((st: RootState) => st.auth?.activeUser?.currentPolicy?.[0]);

    function viewPolicy(preferredLanguageSk?: string): void {
        client
            .query({
                query: GetPolicyQuery,
                variables: {
                    pk: currentPolicy?.pk,
                    sk: preferredLanguageSk || currentPolicy?.sk,
                },
            })
            .then((response) => {
                setState({
                    ...state,
                    loading: false,
                    policy: response.data.getSubscriberPP,
                    policyData: response.data.getSubscriberPP?.content,
                });
            })
            .catch((error) => {
                pageStatus.error(t(CIError.fromGraphQLError(error)?.translationKey));
                setState({ ...initialState, loading: false });
            });
    }

    function getTranslatedPolicy(): void {
        if (user.preferredLanguage) {
            client
                .query({
                    query: CheckPPTranslationQuery,
                    variables: {
                        pk: currentPolicy?.pk,
                        majorVersion: user.currentPolicy[0].majorVersion,
                        language: user.preferredLanguage,
                    },
                })
                .then((response) => {
                    const preferredLanguageSk = response.data.listSubscriberPPs.items[0]?.sk;
                    viewPolicy(preferredLanguageSk);
                });
        } else {
            viewPolicy();
        }
    }

    useEffect(() => {
        if (!user || !userMustAcceptPrivacyPolicy(user)) {
            onAccepted();
        } else {
            getTranslatedPolicy();
        }
    }, []);

    const enableButton = useCallback(() => {
        const policyElement = policyRef.current;
        const isScrollTooSmall =
            state.policyData && policyElement && policyElement.scrollHeight - policyElement.clientHeight < 8;
        if (
            state.policyData &&
            policyElement &&
            (policyElement.scrollHeight <= policyElement.clientHeight || isScrollTooSmall)
        ) {
            setState((prevVals) => ({ ...prevVals, isButtonDisabled: false }));
        }
    }, [state.policyData]);

    useEffect(() => {
        if (state.isLoading && authData?.complete) {
            setState({ ...state, isLoading: false, isButtonDisabled: true });
            CILogger.getInstance().info(
                {
                    message: 'User accepted policy.',
                },
                { userId: authData.activeUser?.pk }
            );

            CIAnalytics.getInstance().identifyUser(authData.activeUser);

            onAccepted();
        }
        const errorMessage = authData?.error;
        if (errorMessage) {
            setState({
                ...state,
                isLoading: false,
                isPolicyClick: false,
                isButtonDisabled: false,
            });
            pageStatus.error(t(errorMessage.translationKey));
        }
    }, [authData?.submitted]);

    useLayoutEffect(() => {
        enableButton();

        window.addEventListener('resize', enableButton, true);

        return () => window.removeEventListener('resize', enableButton);
    }, [enableButton]);

    function handlePolicyScroll(event): void {
        const { scrollTop, clientHeight, scrollHeight } = event.target;
        const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 5;
        if (isAtBottom) {
            setState({
                ...state,
                isButtonDisabled: false,
            });
        }
    }

    function submitPolicyAcceptance(): void {
        dispatch(
            AuthActions.policyRequest({
                ...authData,
                activeUser: {
                    ...authData.activeUser,
                    acceptedVersionPP: currentPolicy?.majorVersion,
                },
            })
        );
    }

    function onPolicyClick(): void {
        setState({
            ...state,
            isPolicyClick: true,
            isLoading: true,
        });
    }

    useEffect(() => {
        state.isPolicyClick && submitPolicyAcceptance();
    }, [state.isPolicyClick]);

    const { updatedAt } = state.policy;
    const { isLoading, isButtonDisabled, policyData } = state;

    return (
        <>
            {fullViewportBackground && <FullViewportContainer aria-hidden="true" />}
            <PolicyContainer>
                <SessionTimeoutDialog />
                {state.loading || forceLoading ? (
                    <div className="loader">{t('policy.loading')}</div>
                ) : (
                    <>
                        {policyData && (
                            <>
                                <div className="title_container">
                                    <h1 className="title">{t('policy.sub_policy_heading')}</h1>
                                    <h6 className="updatedAt">
                                        {updatedAt && `${t('policy.updated')} ${formatDate('MMM D, YYYY', updatedAt)}`}
                                    </h6>
                                </div>
                                <div
                                    id="view_policy"
                                    className="froala_content_view"
                                    ref={policyRef}
                                    dangerouslySetInnerHTML={{ __html: preprocessHtmlText(policyData) }}
                                    onScroll={handlePolicyScroll}
                                ></div>
                                <div className="policy_button_container">
                                    {secondaryAction && (
                                        <CITertiaryButton
                                            className="policy_not_accept_button"
                                            type="button"
                                            id="policyNotAcceptButton"
                                            fullWidth
                                            disabled={isButtonDisabled}
                                            onClick={secondaryAction.onClick}
                                        >
                                            {secondaryAction.label}
                                        </CITertiaryButton>
                                    )}
                                    <CISecondaryButton
                                        className="policy_button"
                                        type="button"
                                        id="policyButton"
                                        fullWidth
                                        disabled={isButtonDisabled}
                                        onClick={onPolicyClick}
                                    >
                                        {!isLoading ? t('policy.accept') : <CircularProgress size={30} />}
                                    </CISecondaryButton>
                                </div>
                            </>
                        )}
                    </>
                )}
            </PolicyContainer>
        </>
    );
};

const FullViewportContainer = styled('div', { name: 'PolicyViewerFullViewportContainer' })(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: -1,
    background: theme.ciPalette.background.fullWhite,
}));

const PolicyContainer = styled('div', { name: 'PolicyViewerContainer' })(({ theme }) => ({
    background: theme.ciPalette.background.fullWhite,
    paddingBottom: '2rem',
    '& .title_container': {
        margin: '16px 0px 32px 40px',
        '.rtl &': {
            margin: '16px 40px 32px 0px',
        },
        '& .title': {
            fontSize: '1.75rem',
        },
        '& .updatedAt': {
            marginTop: '16px',
            color: theme.ciPalette.lightGrey.text,
        },
    },
    '& .policy_button': {
        width: '180px',
        float: 'right',
        minHeight: '62px',
        margin: '32px 45px 18px 0px',
        textTransform: 'none',

        '.rtl &': {
            float: 'left',
            margin: '32px 0px 18px 45px',
        },

        '&.Mui-disabled': {
            backgroundColor: `${theme.ciPalette.brand.blue} !important`,
            color: `${theme.ciPalette.background.fullWhite} !important`,
            opacity: 0.5,
        },
    },
    '& .loader': {
        fontSize: '1.8rem',
        transform: 'translate(-50%, -50%)',
        position: 'absolute',
        left: '50%',
        top: '50%',
    },
    '& #view_policy': {
        overflowY: 'auto',
        border: 'none',
        margin: '0 auto',
        padding: '0px 32px 0px 32px',
        width: '99%',
        '& *': {
            width: '100% !important',
        },
    },
    '& .MuiCircularProgress-svg': {
        color: theme.ciPalette.common.white,
    },
    [theme.breakpoints.down('lg')]: {
        padding: theme.spacing(6.25),
        '& .title_container': {
            margin: '16px 32px 16px 32px !important',
        },
        '& .policy_button_container': {
            display: 'flex',
            justifyContent: 'center',
            paddingLeft: theme.spacing(3.75),
            '.rtl &': {
                paddingRight: theme.spacing(3.75),
                paddingLeft: 0,
            },
            '& .policy_button': {
                height: '42px',
                width: '90%',
            },
        },
    },
    [theme.breakpoints.down('sm')]: {
        padding: 0,
    },
}));
